import {format, isValid} from "date-fns";

import {AppMagicFiltersDateRangeValueProps, AroundTimeProps} from "common/AppMagicFilters/Addons/types";
import {DateRangeTypesEnum} from "types/global";

// e.g. "GMT+4" — timestamps render in the viewer's local timezone, and the
// UI labels them so support can compare against server logs unambiguously.
export const getTimezoneLabel = () => {
    try {
        return new Intl.DateTimeFormat('en', {timeZoneName: 'shortOffset'})
            .formatToParts(new Date())
            .find((part) => part.type === 'timeZoneName')?.value ?? '';
    } catch {
        return '';
    }
};

export const formatTableDateTime = (value?: string | number | null) => {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    return isValid(date) ? format(date, 'MM/dd/yyyy HH:mm:ss') : `${value}`;
};

export const handleDateRange = (date: AppMagicFiltersDateRangeValueProps) => {
    const now = new Date();

    if (date?.type === DateRangeTypesEnum.AROUND_TIME) {
        const aroundTime = {
            dateTime: date?.aroundTime?.dateTime?.toString() ?? null,
            value: date?.aroundTime?.value ?? null,
            period: date?.aroundTime?.period ?? null,
        };
        const dateValue = getAroundDateFromTo(aroundTime);
        return {
            type: date?.type,
            from: dateValue.from ?? null,
            to: dateValue.to ?? null,
            aroundTime,
        };
    }


    if (date?.type === DateRangeTypesEnum.CUSTOM) {
        return {
            type: date?.type,
            from: date?.from?.toString(),
            to: date?.to?.toString(),
        };
    }

    const from = new Date(now);

    const durationMap: Partial<Record<DateRangeTypesEnum, () => void>> = {
        [DateRangeTypesEnum.LAST_5_MINUTES]: () => from.setMinutes(now.getMinutes() - 5),
        [DateRangeTypesEnum.LAST_10_MINUTES]: () => from.setMinutes(now.getMinutes() - 10),
        [DateRangeTypesEnum.LAST_15_MINUTES]: () => from.setMinutes(now.getMinutes() - 15),
        [DateRangeTypesEnum.LAST_30_MINUTES]: () => from.setMinutes(now.getMinutes() - 30),
        [DateRangeTypesEnum.LAST_1_HOUR]: () => from.setHours(now.getHours() - 1),
        [DateRangeTypesEnum.LAST_4_HOURS]: () => from.setHours(now.getHours() - 4),
        [DateRangeTypesEnum.LAST_12_HOURS]: () => from.setHours(now.getHours() - 12),
        [DateRangeTypesEnum.LAST_1_DAY]: () => from.setDate(now.getDate() - 1),
        [DateRangeTypesEnum.LAST_1_MONTH]: () => from.setMonth(now.getMonth() - 1),
        [DateRangeTypesEnum.LAST_3_MONTHS]: () => from.setMonth(now.getMonth() - 3),
        [DateRangeTypesEnum.LAST_6_MONTHS]: () => from.setMonth(now.getMonth() - 6),
        [DateRangeTypesEnum.LAST_12_MONTHS]: () => from.setMonth(now.getMonth() - 12),
    };

    const isDurationType = (type: DateRangeTypesEnum): type is keyof typeof durationMap =>
        Object.prototype.hasOwnProperty.call(durationMap, type);

    if (date?.type && isDurationType(date?.type)) {
        durationMap[date?.type]!();

        return {
            type: date?.type,
            from: from.toString(),
            to: now.toString(),
        };
    }

    return null;
};


export const getAroundDateFromTo = ({dateTime, value, period}: AroundTimeProps) => {
    if (dateTime && value && period) {
        const baseDate = new Date(dateTime);
        const from = new Date(baseDate);
        const to = new Date(baseDate);

        switch (period) {
            case 'MINUTES':
                from.setMinutes(baseDate.getMinutes() - value);
                to.setMinutes(baseDate.getMinutes() + value);
                break;
            case 'HOURS':
                from.setHours(baseDate.getHours() - value);
                to.setHours(baseDate.getHours() + value);
                break;
            case 'DAYS':
                from.setDate(baseDate.getDate() - value);
                to.setDate(baseDate.getDate() + value);
                break;
            case 'WEEKS':
                from.setDate(baseDate.getDate() - value * 7);
                to.setDate(baseDate.getDate() + value * 7);
                break;
            default:
                throw new Error(`Unsupported period: ${period}`);
        }

        return {
            from: from.toString(),
            to: to.toString(),
        };
    }
    return {
        from: null,
        to: null,
    };
}


export const toDateTimeLocalString = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
}
